/**
 * CCS Jury Expert — Bibliothèque de fiches Plateau (C1) + Circulation (C2/C3)
 * Source : PDF "CCS 2 Roues 5 Fiches Prioritaires" + infographies 7 familles + 9 étapes.
 * Chaque fiche suit le canevas répétitif que le jury attend.
 */

export const CCS_CANEVAS = {
  etapes9: [
    { n: 1, label: "J'observe (10 min de circulation)" },
    { n: 2, label: "Je fais parler (auto-évaluation)" },
    { n: 3, label: "Je valide l'objectif avec l'élève" },
    { n: 4, label: "Je pose l'objectif" },
    { n: 5, label: "Je donne les 4 critères" },
    { n: 6, label: "Je choisis 1 critère travaillé" },
    { n: 7, label: "Je propose un exercice" },
    { n: 8, label: "Guidage dégressif (FORT → FAIBLE)" },
    { n: 9, label: "Bilan (acquis / à travailler / suite)" },
  ],
  guidage4: [
    { n: 1, nom: 'TOTAL',    desc: 'Je dis tout, je montre, j\'explique', color: '#dc2626' },
    { n: 2, nom: 'PARTIEL',  desc: 'Je donne un mot-clé, une indication', color: '#f59e0b' },
    { n: 3, nom: 'MINIMAL',  desc: 'J\'observe, j\'interviens si besoin', color: '#16a34a' },
    { n: 4, nom: 'AUTONOME', desc: 'L\'élève réalise seul', color: '#0891b2' },
  ],
  phraseJury: "Lors de l'évaluation dynamique, j'ai observé un écart entre le comportement attendu et le comportement réalisé. Après auto-évaluation de l'élève, nous avons retenu ensemble cet objectif afin de développer sa compétence dans cette situation de conduite.",
  questionsJury: [
    { q: 'Pourquoi cet objectif ?', r: "Parce qu'il est issu de mes observations et de l'auto-évaluation de l'élève. Il a été validé ensemble." },
    { q: 'Pourquoi ces 4 critères ?', r: 'Ce sont les éléments indispensables à la réussite de la compétence visée.' },
    { q: 'Pourquoi ce critère travaillé ?', r: "C'est le critère prioritaire, frein principal à la réussite de l'élève." },
    { q: 'Pourquoi cet exercice ?', r: "Il permet de répéter le comportement attendu dans une situation réelle et sécurisée." },
    { q: "Comment vérifiez-vous l'acquisition ?", r: "Par l'observation des critères et l'auto-évaluation finale de l'élève." },
  ],
};

const f = (epreuve, slug, num, emoji, titre, motCle, data) => ({
  epreuve, slug, num, emoji, titre, motCle, ...data,
});

/**
 * Sélection "Top 10 cours les plus simples à donner à l'oral CCS 2 Roues"
 * Identifiés par le flag `topFacile: true` ci-dessous.
 *
 * Critères de sélection :
 *  - Principe pédagogique très clair (1 phrase suffit pour le poser au jury)
 *  - Démonstration possible en moins de 5 minutes
 *  - Élève-test n'a besoin d'aucun pré-requis technique avancé
 *  - Critères observables faciles à évaluer
 *  - Faible risque de chute / d'erreur élève-test
 */
export const FICHES_PLATEAU_CCS = [
  f('plateau','mise-en-mouvement',1,'🎯',"Savoir mettre la moto en mouvement et s'arrêter avec précision",'Patinage', {
    topFacile: true,
    situation: ['Calages','Départs brusques',"Arrêts imprécis"],
    questions: ["Te sens-tu à l'aise à l'embrayage ?","Qu'est-ce qui te bloque au démarrage ?"],
    criteres: ['Préparer la moto',"Utiliser la zone de patinage","Doser l'accélération",'Immobiliser avec précision'],
    critereTravaille: "Utiliser la zone de patinage",
    pourquoi: 'Permet un départ progressif sans calage.',
    comment: "Relâcher progressivement l'embrayage jusqu'au point de patinage.",
    risque: 'Calage ou départ brutal.',
    benefice: 'Moto maîtrisée au démarrage.',
    exercice: "Départ-arrêt tous les 20 mètres, arrêt à ≤ 1m du repère",
    guidage: ["1-embraye, 2-gaz, 3-pars",'« Patinage »','(je regarde ton arrêt)','Autonomie'],
  }),
  f('plateau','allure-lente',2,'🎯',"Savoir maîtriser l'allure lente",'Loin',{
    topFacile: true,
    situation: ['Pied posé','Oscillations',"Perte d'équilibre"],
    questions: ["Où regardais-tu pendant la manœuvre ?","As-tu utilisé le frein arrière ?"],
    criteres: ['Regard loin','Zone de patinage','Action frein arrière',"Équilibre conservé"],
    critereTravaille: 'Regard loin',
    pourquoi: "La stabilité vient du regard porté loin.",
    comment: "Je fixe un point devant moi, pas les commandes.",
    risque: 'Regard proche = pied au sol immédiat.',
    benefice: 'Moto stable même à 5 km/h.',
    exercice: 'Couloir lent de 20m sans poser le pied',
    guidage: ['« Loin »','« Patine »','(silence)','Autonomie'],
  }),
  f('plateau','coordination-commandes',3,'🎯','Savoir coordonner les commandes','Coordonne',{
    situation: ['Gestes brusques','Mauvaise coordination embrayage/gaz'],
    questions: ["Quelle commande te pose le plus de problème ?"],
    criteres: ['Embrayage progressif','Gaz dosé','Frein arrière utile','Regard porté loin'],
    critereTravaille: 'Coordination embrayage / accélérateur',
    pourquoi: "Une moto se pilote en simultané pas en séquence.",
    comment: "J'apprends à doser l'embrayage et le gaz en même temps.",
    risque: 'Saccades = perte de stabilité.',
    benefice: 'Moto fluide et prévisible.',
    exercice: 'Parcours lent avec changements de rythme',
    guidage: ['« Embraye + gaz »','« Coordonne »','(silence)','Autonomie'],
  }),
  f('plateau','regard-plateau',4,'🎯','Savoir utiliser le regard pour diriger la moto','Regarde',{
    topFacile: true,
    situation: ['Regard proche','Regard fixé sur un cône'],
    questions: ["Où portais-tu ton regard ?"],
    criteres: ["Regard vers l'objectif",'Rotation de la tête','Anticipation','Fluidité du mouvement'],
    critereTravaille: "Regard vers le point d'arrivée",
    pourquoi: 'La moto va où le regard se porte.',
    comment: 'Je tourne la tête avant la moto.',
    risque: "Fixation sur l'obstacle = on va dedans.",
    benefice: 'Trajectoire propre et fluide.',
    exercice: 'Slalom lent avec regard porté 2 cônes plus loin',
    guidage: ['« Tourne la tête »','« Loin »','(silence)','Autonomie'],
  }),
  f('plateau','equilibre-faible-vitesse',5,'🎯','Savoir maintenir son équilibre à faible vitesse','Souple',{
    topFacile: true,
    situation: ["Déséquilibres",'Corrections excessives au guidon'],
    questions: ["Tes bras sont-ils tendus ou souples ?"],
    criteres: ['Regard loin','Bras souples','Moto mobile (mini-corrections)','Allure constante'],
    critereTravaille: 'Bras souples',
    pourquoi: 'Bras crispés = moto rigide, perte d\'équilibre.',
    comment: 'Je relâche mes épaules et mes coudes.',
    risque: 'Sur-correction = chute à basse vitesse.',
    benefice: 'La moto trouve son propre équilibre.',
    exercice: 'Ligne droite lente sans poser le pied sur 30m',
    guidage: ['« Détends tes bras »','« Souple »','(silence)','Autonomie'],
  }),
  f('plateau','slalom-allure-reduite',6,'🎯','Savoir réaliser un slalom à allure réduite','Anticipe',{
    topFacile: true,
    situation: ['Cônes touchés','Trajectoire irrégulière'],
    questions: ["À quel cône te concentrais-tu ?"],
    criteres: ['Regard anticipé','Trajectoire régulière','Équilibre maintenu','Allure constante'],
    critereTravaille: 'Regard anticipé',
    pourquoi: 'Le regard 2 cônes plus loin guide la moto.',
    comment: "Je fixe le cône n+2 quand je passe le cône n.",
    risque: 'Regard sur le cône en cours = trajectoire saccadée.',
    benefice: 'Slalom fluide en S.',
    exercice: 'Slalom progressif 5 cônes espacés 4m',
    guidage: ['« Cône suivant »','« Anticipe »','(silence)','Autonomie'],
  }),
  f('plateau','trajectoire-imposee',7,'🎯','Savoir maintenir une trajectoire imposée','Trajectoire',{
    situation: ['Écarts de trajectoire','Sortie de couloir'],
    questions: ["As-tu vu les bords du couloir ?"],
    criteres: ['Regard porté loin','Positionnement précis','Équilibre maintenu','Régularité'],
    critereTravaille: 'Maintenir la trajectoire',
    pourquoi: "Une trajectoire précise = un examen réussi.",
    comment: "Je vise la sortie du couloir, pas les bords.",
    risque: 'Regard latéral = dérive latérale.',
    benefice: 'Trajectoire maîtrisée même sous stress.',
    exercice: 'Couloir matérialisé 60cm de large sur 30m',
    guidage: ['« Vise la sortie »','« Trajectoire »','(silence)','Autonomie'],
  }),

  // ───── 5 NOUVEAUX OBJECTIFS PLATEAU axés Regard · Trajectoire · Allure ─────
  f('plateau','demi-tour-a-pied',8,'👣','Réaliser un demi-tour à pied (moto à 1 km/h)','Pas-à-pas',{
    topFacile: true, themeRTA: true,
    situation: ['Demi-tour à grande largeur','Pied posé brutalement','Trajectoire en cercle irrégulier'],
    questions: ["Où regardes-tu en sortant du demi-tour ?"],
    criteres: ['Regard porté à la sortie','Trajectoire en U régulière','Allure pas-à-pas constante','Bras souples'],
    critereTravaille: 'Regard à la sortie + allure pas',
    pourquoi: "À très basse vitesse, c'est le regard qui guide la moto, pas le guidon.",
    comment: "Je fixe la zone de sortie 5m après le sommet du demi-tour, allure tout doux à la friction d'embrayage.",
    risque: "Regard vers les pieds = trajectoire fermée et chute moteur.",
    benefice: "Demi-tour propre en U, aucune correction guidon.",
    exercice: "Demi-tour à pied dans un carré 4×4m, moteur tournant à la friction.",
    guidage: ['« Regarde où tu veux aller »','« Pas-à-pas »','(silence court)','Autonomie'],
  }),
  f('plateau','evitement-allure-reduite',9,'🚧','Éviter un obstacle à allure réduite','Anticipe',{
    topFacile: true, themeRTA: true,
    situation: ['Réaction tardive','Trajectoire cassée','Coup de guidon brusque'],
    questions: ["À quel moment as-tu vu la sortie d'évitement ?"],
    criteres: ['Regard porté au-delà de l\'obstacle','Trajectoire fluide en S','Allure constante avant + après','Inclinaison maîtrisée'],
    critereTravaille: 'Regard au-delà de l\'obstacle',
    pourquoi: "Le regard anticipé déclenche le mouvement naturel d'évitement.",
    comment: "Je ne fixe jamais le cône — je vise toujours 5m plus loin, où je veux aller.",
    risque: "Fixer le cône = la moto va dessus (champ tunnel).",
    benefice: "Évitement souple sans cassure d'allure.",
    exercice: "Évitement gauche d'un cône à 20 km/h, sortie ligne droite obligatoire.",
    guidage: ['« Regarde après le cône »','« Plus loin »','(silence)','Autonomie'],
  }),
  f('plateau','passage-couloir-etroit',10,'🛤️','Franchir un couloir étroit à allure dosée','Précision',{
    topFacile: true, themeRTA: true,
    situation: ['Sorties de couloir','Cônes touchés','Allure trop rapide ou trop lente'],
    questions: ["Comment doses-tu ton allure dans le couloir ?"],
    criteres: ['Regard fixé à la sortie','Trajectoire centrée','Allure dosée à la friction','Équilibre maintenu'],
    critereTravaille: 'Dosage allure friction',
    pourquoi: "Plus c'est étroit, plus le dosage embrayage/frein arrière stabilise la moto.",
    comment: "J'allume la friction d'embrayage et je freine légèrement à l'arrière pour maintenir une allure régulière.",
    risque: "Allure trop lente = chute. Trop rapide = sortie du couloir.",
    benefice: "Couloir franchi droit comme un i, allure parfaitement régulière.",
    exercice: "Couloir 45cm de large × 25m, allure pas plus vite qu'un marcheur.",
    guidage: ['« Friction + frein arrière »','« Dose »','(silence)','Autonomie'],
  }),
  f('plateau','arret-precis-ligne',11,'⏹️','S\'arrêter sur une ligne précise sans pied au sol','Pile',{
    topFacile: true, themeRTA: true,
    situation: ['Arrêt avant la ligne','Arrêt après la ligne','Pied posé en avance'],
    questions: ["Où regardes-tu pendant la phase d'arrêt ?"],
    criteres: ['Regard fixé sur la ligne d\'arrêt','Trajectoire droite jusqu\'au bout','Allure dégressive douce','Pied gauche posé après arrêt complet'],
    critereTravaille: 'Allure dégressive + regard ligne',
    pourquoi: "L'œil sur la cible affine instinctivement le freinage.",
    comment: "Je relâche les gaz, je freine progressivement les deux freins, je garde le regard sur la ligne.",
    risque: "Regard sur le tableau de bord = anticipation perdue, arrêt imprécis.",
    benefice: "Arrêt pile sur la ligne, exigence examen.",
    exercice: "Ligne au sol, arrivée à 30 km/h, arrêt pile pneu avant sur la ligne.",
    guidage: ['« Regard sur la ligne »','« Freine doux »','(silence)','Autonomie'],
  }),
  f('plateau','demi-tour-en-u',12,'🔄','Réaliser un demi-tour en U à allure modérée','Sortie',{
    topFacile: true, themeRTA: true,
    situation: ['Demi-tour ouvert','Pied posé','Allure trop rapide en entrée'],
    questions: ["Où portes-tu ton regard quand tu attaques le demi-tour ?"],
    criteres: ['Regard tourné vers la sortie','Trajectoire en U serrée','Allure ralentie en entrée','Reprise progressive en sortie'],
    critereTravaille: 'Regard sortie + trajectoire U',
    pourquoi: "La moto suit le regard. Si je regarde la sortie, le U se ferme tout seul.",
    comment: "J'attaque le demi-tour à 15 km/h, regard tourné vers la sortie dès le début, friction + frein arrière.",
    risque: "Regard à l'intérieur de la courbe = trajectoire ouverte, sortie de zone.",
    benefice: "Demi-tour serré sans poser le pied, conforme examen.",
    exercice: "Demi-tour en U dans une zone 6×3m délimitée par 4 cônes.",
    guidage: ['« Regarde la sortie »','« Friction »','(silence)','Autonomie'],
  }),
];

export const FICHES_CIRCULATION_CCS = [
  f('circulation','regard-sortie-virage',1,'👁','Savoir porter son regard vers la sortie du virage','Sortie',{
    topFacile: true,
    famille: 'Regard',
    situation: ['Regard proche','Regard au point de corde','Regard fixé sur un obstacle'],
    questions: ["Où regardais-tu ?","Qu'as-tu vu ?","Qu'est-ce qui t'a gêné ?"],
    criteres: ["Rechercher l'information loin devant","Tourner la tête vers la sortie avant l'inclinaison","Balayer l'environnement",'Maintenir une trajectoire régulière'],
    critereTravaille: 'Tourner la tête vers la sortie',
    pourquoi: 'La moto suit naturellement le regard.',
    comment: 'Je tourne la tête avant la moto.',
    risque: 'Fixation = trajectoire dégradée.',
    benefice: 'Trajectoire plus fluide et sereine.',
    exercice: '5 virages successifs en route sinueuse',
    guidage: ['« Regarde la sortie »','« Sortie »','(silence)','Autonomie'],
  }),
  f('circulation','allure-avant-virage',2,'⚡','Savoir adapter son allure avant un virage','Avant',{
    topFacile: true,
    famille: 'Allure',
    situation: ['Entrée trop rapide','Freinage tardif dans le virage'],
    questions: ["Quand as-tu ralenti ?","Te sentais-tu en sécurité ?"],
    criteres: ['Identifier le virage',"Réduire son allure avant l'entrée",'Stabiliser la moto','Conserver une marge de sécurité'],
    critereTravaille: "Réduire son allure avant l'entrée",
    pourquoi: 'Une moto inclinée freine moins efficacement.',
    comment: "Je termine mon freinage avant l'inclinaison.",
    risque: 'Perte de stabilité, chute.',
    benefice: 'Virage plus sûr et plus fluide.',
    exercice: 'Enchaînement de virages avec annonces',
    guidage: ['« Freine avant »','« Avant »','(silence)','Autonomie'],
  }),
  f('circulation','trajectoire-securite',3,'🏍','Savoir adopter une trajectoire de sécurité','Prépare',{
    famille: 'Trajectoire',
    situation: ['Coupe le virage','Sort trop large','Empiète ligne médiane'],
    questions: ["Comment as-tu pris le virage ?","Où regardais-tu ?"],
    criteres: ['Se positionner avant le virage','Adapter son allure',"Porter son regard vers la sortie",'Revenir progressivement sur sa voie'],
    critereTravaille: 'Positionnement avant le virage',
    pourquoi: 'Une bonne trajectoire = plus de visibilité.',
    comment: 'Je suis les 4 phases : Préparation → Entrée → Corde → Sortie.',
    risque: 'Couper le virage = collision frontale.',
    benefice: 'Plus de visibilité et de sécurité.',
    exercice: 'Virages successifs avec placement annoncé',
    guidage: ['« Place-toi extérieur »','« Prépare »','(silence)','Autonomie'],
  }),
  f('circulation','controles-changement',4,'🔍','Savoir contrôler avant un changement de direction','RACS',{
    topFacile: true,
    famille: 'Contrôles',
    situation: ['Oubli angle mort','Contrôles incomplets','Clignotant tardif'],
    questions: ["Quels contrôles as-tu réalisés ?"],
    criteres: ['Contrôler le rétroviseur',"Contrôler l'angle mort",'Signaler son intention','Adapter son placement'],
    critereTravaille: 'Contrôle angle mort',
    pourquoi: "L'angle mort est invisible des rétros.",
    comment: 'Rétro → Angle mort → Clignotant → Se placer.',
    risque: 'Collision latérale avec véhicule masqué.',
    benefice: 'Sécuriser tout déplacement latéral.',
    exercice: 'Plusieurs changements de direction en série',
    guidage: ['« RACS complet »','« RACS »','(silence)','Autonomie'],
  }),
  f('circulation','identifier-risques',5,'⚠','Savoir identifier et anticiper les risques','Indice',{
    topFacile: true,
    famille: 'Risques',
    situation: ['Piéton masqué','Zone d\'incertitude ignorée'],
    questions: ["Quels dangers as-tu vus ?",'Quels dangers aurais-tu pu rencontrer ?'],
    criteres: ['Identifier les indices de danger','Analyser la situation','Adapter son allure',"Préparer une solution de sécurité"],
    critereTravaille: 'Identifier les indices de danger',
    pourquoi: "Le danger est souvent annoncé avant d'apparaître.",
    comment: 'Je cherche les indices : véhicule garé, ballon, école, arrêt bus.',
    risque: 'Réaction tardive = accident.',
    benefice: 'Temps de réaction multiplié.',
    exercice: 'Zone urbaine avec risques potentiels',
    guidage: ['« Cherche les indices »','« Indice »','(silence)','Autonomie'],
  }),
  f('circulation','freinage-progressif',6,'🛑','Savoir freiner de manière progressive','Doser',{
    topFacile: true,
    famille: 'Allure',
    situation: ['Freinage brutal','Moto déséquilibrée','Roue arrière qui bloque'],
    questions: ["As-tu utilisé les deux freins ?"],
    criteres: ['Regard loin','Utiliser les deux freins','Doser les commandes',"Maintenir l'équilibre"],
    critereTravaille: 'Dosage du freinage',
    pourquoi: '70% avant + 30% arrière = freinage optimal moto.',
    comment: "J'attaque ARRIÈRE en premier, puis je viens chercher l'AVANT progressivement.",
    risque: 'Blocage = chute.',
    benefice: 'Distance d\'arrêt minimale.',
    exercice: "Freinage d'arrêt progressif depuis 50 km/h",
    guidage: ['« Arrière puis avant »','« Doser »','(silence)','Autonomie'],
  }),
  f('circulation','commandes-precision',7,'⚙','Savoir utiliser les commandes avec précision','Précision',{
    famille: 'Contrôles',
    situation: ['Saccades','Sur-révision','Sous-utilisation des freins'],
    questions: ["Quelle commande te paraît la plus délicate ?"],
    criteres: ["Utiliser l'embrayage correctement",'Doser les gaz','Utiliser le frein adapté','Coordonner les actions'],
    critereTravaille: 'Coordination des commandes',
    pourquoi: 'La moto se pilote en symbiose des 4 commandes.',
    comment: "Je chronique chaque action à voix haute pour conscientiser.",
    risque: 'Action contradictoire = perte de contrôle.',
    benefice: 'Conduite fluide et économique.',
    exercice: 'Boucle urbaine avec verbalisation',
    guidage: ['« Verbalise »','« Précision »','(silence)','Autonomie'],
  }),
  f('circulation','stabilite-moto',8,'⚖','Savoir maintenir la stabilité de la moto','Stable',{
    famille: 'Allure',
    situation: ['Oscillations','Corrections nerveuses','Crispation'],
    questions: ["Tes bras étaient-ils tendus ?"],
    criteres: ['Regard loin','Bras souples','Allure adaptée','Commandes progressives'],
    critereTravaille: 'Souplesse du buste',
    pourquoi: 'La moto stabilise d\'elle-même si on la laisse faire.',
    comment: 'Je détends les bras, je serre les genoux au réservoir.',
    risque: 'Crispation = oscillations dangereuses.',
    benefice: 'Conduite reposante et sûre.',
    exercice: 'Ligne droite + virage à allure constante',
    guidage: ['« Détend-toi »','« Stable »','(silence)','Autonomie'],
  }),
  f('circulation','enchainer-virages',9,'🔁','Savoir enchaîner plusieurs virages','Enchaîne',{
    famille: 'Trajectoire',
    situation: ['Reset entre chaque virage','Trajectoire saccadée'],
    questions: ["Pensais-tu déjà au virage suivant ?"],
    criteres: ['Regard anticipé','Allure adaptée','Placement cohérent','Trajectoire régulière'],
    critereTravaille: "Regard anticipé sur virage n+1",
    pourquoi: "Anticiper le n+1 fluidifie le n.",
    comment: "En sortie de virage, mon regard cherche déjà la prochaine entrée.",
    risque: 'Reset = perte de temps + déstabilisation.',
    benefice: 'Conduite sportive et sûre.',
    exercice: 'Route sinueuse 5 virages alternés',
    guidage: ['« Suivant »','« Enchaîne »','(silence)','Autonomie'],
  }),
  f('circulation','positionnement-chaussee',10,'📍','Savoir se positionner en fonction des risques','Visible',{
    famille: 'Positionnement',
    situation: ['Trop à droite','Trop à gauche','Sur ligne médiane'],
    questions: ["Où te plaçais-tu ?",'Étais-tu visible ?'],
    criteres: ['Être visible','Voir','Être vu',"Préparer l'action suivante"],
    critereTravaille: 'Position adaptée au contexte',
    pourquoi: 'Hors agglo : 1/3 droit. Agglo : centre/1/3 gauche. Jamais sur la médiane.',
    comment: "J'adapte selon le contexte : route ouverte vs urbain dense.",
    risque: 'Position fixe = inadaptée à 50% des situations.',
    benefice: 'Visible + sécurisé partout.',
    exercice: 'Route variée : ligne droite + virages + intersections',
    guidage: ['« Place-toi »','« Visible »','(silence)','Autonomie'],
  }),
  f('circulation','danger-cache',11,'🚸','Savoir anticiper un danger caché','Masque',{
    famille: 'Risques',
    situation: ["Camion qui masque",'Voiture garée masquante','Sortie de garage'],
    questions: ["Quels masques visuels as-tu identifiés ?"],
    criteres: ['Identifier le masque visuel',"Réduire l'allure",'Préparer le freinage','Préserver une marge'],
    critereTravaille: 'Identification du masque visuel',
    pourquoi: 'Tout ce que je ne vois pas peut sortir.',
    comment: 'Je traite chaque masque comme "piéton potentiel".',
    risque: 'Choc avec usager invisible.',
    benefice: 'Temps de réaction préservé.',
    exercice: 'Circuit urbain avec masques multiples',
    guidage: ['« Que ne vois-tu pas ? »','« Masque »','(silence)','Autonomie'],
  }),
  f('circulation','depassement',12,'🚙','Savoir réaliser un dépassement en sécurité','Décide',{
    famille: 'Trajectoire',
    situation: ['Hésitation','Dépassement entamé puis annulé','Marge insuffisante'],
    questions: ["Avais-tu la visibilité ?",'Avais-tu le temps ?'],
    criteres: ['Observer','Contrôler','Dépasser franchement','Revenir progressivement'],
    critereTravaille: 'Décision et engagement',
    pourquoi: 'Un dépassement hésitant est plus dangereux qu\'un dépassement franc.',
    comment: 'Je décide AVANT de me déporter. Une fois engagé, je vais jusqu\'au bout.',
    risque: 'Hésitation = collision face.',
    benefice: 'Dépassement net et bref.',
    exercice: 'Dépassement de véhicule lent en sécurité',
    guidage: ['« Visibilité + temps ? »','« Décide »','(silence)','Autonomie'],
  }),
  f('circulation','giratoire',13,'🔄','Savoir franchir un giratoire en sécurité','Engagé',{
    famille: 'Trajectoire',
    situation: ['Hésitation à l\'entrée','Trajectoire confuse','Oubli clignotant sortie'],
    questions: ["Qui avait la priorité ?","Quand as-tu mis ton clignotant ?"],
    criteres: ['Observer','Adapter l\'allure','Choisir la trajectoire','Signaler la sortie'],
    critereTravaille: 'Trajectoire dans le giratoire',
    pourquoi: 'Trajectoire claire = lisible pour les autres.',
    comment: "Je m'engage seulement si la voie est libre, et je sors clignotant après la sortie précédente.",
    risque: 'Trajectoire confuse = collision.',
    benefice: 'Giratoire fluide pour tous.',
    exercice: 'Plusieurs giratoires en circuit',
    guidage: ['« Engage-toi nettement »','« Engagé »','(silence)','Autonomie'],
  }),
  f('circulation','communication',14,'📢','Savoir communiquer ses intentions','Compris',{
    famille: 'Communication',
    situation: ['Clignotant tardif','Intentions illisibles','Mauvaise interaction'],
    questions: ["Les autres ont-ils compris ?"],
    criteres: ['Être vu','Être compris','Utiliser les clignotants','Adapter son comportement'],
    critereTravaille: 'Lisibilité des intentions',
    pourquoi: 'Les autres adaptent leur conduite si je suis lisible.',
    comment: 'Je signale TÔT et FRANCHEMENT.',
    risque: 'Intentions floues = manœuvres conflictuelles.',
    benefice: 'Circulation fluide et sécurisée.',
    exercice: 'Insertion + dépassement + giratoire avec verbalisation',
    guidage: ['« Sois lisible »','« Compris »','(silence)','Autonomie'],
  }),
  f('circulation','hierarchiser-risques',15,'🎚','Savoir hiérarchiser les risques','Priorité',{
    famille: 'Risques',
    situation: ['Traite tout également','Saute un risque majeur'],
    questions: ["Quel était le risque principal ?"],
    criteres: ['Identifier les risques','Classer les priorités','Traiter le risque principal','Réévaluer la situation'],
    critereTravaille: 'Hiérarchisation',
    pourquoi: 'On ne peut pas tout traiter en même temps.',
    comment: 'Je traite d\'abord le risque le plus probable + le plus grave.',
    risque: 'Surcharge cognitive = oubli vital.',
    benefice: "Conduite priorisée et sereine.",
    exercice: 'Carrefour complexe + piétons + véhicules',
    guidage: ['« Quel risque d\'abord ? »','« Priorité »','(silence)','Autonomie'],
  }),

  // ───── 5 NOUVEAUX OBJECTIFS CIRCULATION axés Regard · Trajectoire · Allure ─────
  f('circulation','giratoire-simple',16,'⭕','Aborder un giratoire simple à 1 voie','Couler',{
    topFacile: true, themeRTA: true,
    famille: 'Allure',
    situation: ['Entrée trop rapide','Trajectoire en escalier','Regard fixé devant'],
    questions: ["Où regardais-tu à l'entrée du giratoire ?"],
    criteres: ['Regard porté vers la sortie choisie','Trajectoire fluide en arc','Allure adaptée en entrée','Sortie progressive (gaz)'],
    critereTravaille: 'Regard à la sortie + allure entrée',
    pourquoi: "Le giratoire se négocie au regard avant de se négocier au guidon.",
    comment: "À l'approche, je ralentis (frein moteur), je tourne la tête vers la sortie souhaitée, je laisse la moto suivre.",
    risque: "Regard sur la circulation à l'intérieur = trajectoire en escalier.",
    benefice: "Giratoire propre, fluide, sécurisant.",
    exercice: "3 passages successifs dans un giratoire urbain simple, 3 sorties différentes.",
    guidage: ['« Regarde ta sortie »','« Coule »','(silence)','Autonomie'],
  }),
  f('circulation','tourner-a-droite-intersection',17,'↘️','Tourner à droite à une intersection en T','Serre',{
    topFacile: true, themeRTA: true,
    famille: 'Trajectoire',
    situation: ['Trajectoire trop large','Regard sur les rétros','Allure trop rapide en sortie'],
    questions: ["Où était ton regard 2 secondes avant de tourner ?"],
    criteres: ['Regard porté dans la rue de sortie','Trajectoire serrée près du bord droit','Allure ralentie en entrée','Reprise gaz progressive'],
    critereTravaille: 'Trajectoire serrée + regard rue de sortie',
    pourquoi: "Une trajectoire serrée empêche de mordre la voie inverse — c'est la sécurité.",
    comment: "Je ralentis 20m avant, je regarde dans la rue où je vais, je serre la trajectoire vers le bord droit.",
    risque: "Trajectoire ouverte = empiétement sur la voie inverse = piège examen.",
    benefice: "Virage propre, lisible pour les autres, conforme examen.",
    exercice: "Parcours urbain avec 4 intersections en T à virer à droite.",
    guidage: ['« Ralentis, serre, regarde »','« Serre »','(silence)','Autonomie'],
  }),
  f('circulation','croisement-route-etroite',18,'🚙','Croiser un véhicule sur route étroite','Marge',{
    topFacile: true, themeRTA: true,
    famille: 'Positionnement',
    situation: ['Croisement avec marge nulle','Regard fixé sur le véhicule croisé','Allure non adaptée'],
    questions: ["À quelle distance regardes-tu en croisement ?"],
    criteres: ['Regard porté au-delà du véhicule croisé','Trajectoire serrée à droite','Allure réduite avant croisement','Reprise après croisement'],
    critereTravaille: 'Regard au-delà + allure réduite',
    pourquoi: "Fixer le véhicule croisé attire la moto vers lui (champ tunnel).",
    comment: "Je ralentis avant le point de croisement, je regarde 30m après le véhicule, je serre à droite.",
    risque: "Regard sur le véhicule = trajectoire qui se rapproche = collision frontale.",
    benefice: "Croisement maîtrisé, marge de sécurité préservée.",
    exercice: "Croisement sur route 4m de large, voiture en face à 50 km/h.",
    guidage: ['« Regarde derrière la voiture »','« Au-delà »','(silence)','Autonomie'],
  }),
  f('circulation','reprise-apres-stop',19,'🛑','Repartir d\'un stop en sécurité','Voir',{
    topFacile: true, themeRTA: true,
    famille: 'Contrôles',
    situation: ['Départ sans contrôle complet','Trajectoire qui flotte','Démarrage brusque'],
    questions: ["As-tu vraiment regardé 3 fois ?"],
    criteres: ['Regard gauche-droite-gauche complet','Trajectoire droite à la reprise','Allure progressive (sans à-coup)','Tête tournée, pas juste les yeux'],
    critereTravaille: 'Regard gauche-droite-gauche',
    pourquoi: "À un stop, c'est la qualité du regard qui décide de la sécurité.",
    comment: "Pied au sol, j'arrête complètement, je tourne la tête à gauche puis à droite puis à gauche, puis je redémarre doucement.",
    risque: "Coup d'œil rapide sans tourner la tête = angle mort non couvert.",
    benefice: "Démarrage sûr, contrôles visibles par le jury.",
    exercice: "Parcours avec 5 stops successifs en zone urbaine.",
    guidage: ['« Tourne la tête trois fois »','« Voir »','(silence)','Autonomie'],
  }),
  f('circulation','anticiper-passage-pieton',20,'🚶','Anticiper un passage piéton','Lentement',{
    topFacile: true, themeRTA: true,
    famille: 'Allure',
    situation: ['Allure non dégressive','Regard tardif sur les piétons','Pas de positionnement défensif'],
    questions: ["À quelle distance as-tu vu le passage piéton ?"],
    criteres: ['Regard balayant le trottoir 50m avant','Trajectoire centrée sur la voie','Allure dégressive 30m avant','Doigt sur le frein avant'],
    critereTravaille: 'Allure dégressive + regard piétons',
    pourquoi: "Le piéton est l'usager le plus vulnérable. La marge se gagne en ralentissant TÔT.",
    comment: "Je balaie du regard les trottoirs 50m avant, je relâche les gaz 30m avant, je pose un doigt sur le frein avant.",
    risque: "Arriver à 50 km/h sur un passage piéton = aucune marge en cas de surprise.",
    benefice: "Conduite défensive, marge de sécurité réelle.",
    exercice: "Parcours avec 4 passages piétons consécutifs, vitesse maxi 30 km/h en approche.",
    guidage: ['« Ralentis dès maintenant »','« Lentement »','(silence)','Autonomie'],
  }),
];

export function getCCSFicheBySlug(slug) {
  return [...FICHES_PLATEAU_CCS, ...FICHES_CIRCULATION_CCS].find(f => f.slug === slug);
}
